import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DescricaoPrioridade, PrioridadeEnum } from 'src/app/enum/prioridade.enum';
import { DescricaoStatusTicket, StatusTicketEnum } from 'src/app/enum/statusTicket.enum';
import { DescricaoTipoTicket, TipoTicketEnum } from 'src/app/enum/tipoTicket.enum';
import { Client } from 'src/app/page/client/client';
import { Segmento } from 'src/app/page/segmento/segmento';
import { Usuario } from 'src/app/page/usuario/usuario';

interface UploadedFile {
  name: string;
  preview: any;
  file: File;
}

@Component({
  selector: 'app-ticket-cadastro',
  templateUrl: './ticket-cadastro.component.html',
  styleUrls: ['./ticket-cadastro.component.scss'],
})
export class TicketCadastroComponent {

  @Input() formulario!: FormGroup;
  @Input() segmentos: Segmento[] = [];
  @Input() parceiros: Client[] = [];
  @Input() atribuicoes: Usuario[] = [];
  @Input() prioridades = Object.values(PrioridadeEnum);
  @Input() status = Object.values(StatusTicketEnum);
  @Input() tipoTickets = Object.values(TipoTicketEnum);
  @Input() uploadedFiles: UploadedFile[] = [];
  @Input() isDragOver = false;
  @Input() tempoEstimadoBloqueadoPersistido = false;
  @Input() compararCampos: (a: any, b: any) => boolean = () => false;

  @Output() definirDataPrevisao = new EventEmitter<void>();
  @Output() limpar = new EventEmitter<void>();
  @Output() selecionarArquivos = new EventEmitter<Event>();
  @Output() soltarArquivos = new EventEmitter<DragEvent>();
  @Output() arrastarSobre = new EventEmitter<DragEvent>();
  @Output() sairDoArrasto = new EventEmitter<DragEvent>();
  @Output() removerArquivo = new EventEmitter<number>();
  @Output() baixarArquivo = new EventEmitter<{ file: UploadedFile; index: number }>();

  readonly INTERNO = { id: -1, razaoSocial: 'Interno' };

  imagemAmpliada: any = null;
  nomeImagemAmpliada = '';

  // Parceiros só aparecem se estiverem vinculados ao segmento selecionado.
  get parceirosDoSegmento(): Client[] {
    const segmento = this.formulario?.get('segmento')?.value;
    if (!segmento) {
      return this.parceiros;
    }
    return this.parceiros.filter(parceiro => (parceiro.segmentos || []).includes(segmento));
  }

  aoMudarSegmento(): void {
    const segmento = this.formulario?.get('segmento')?.value;
    const parceiroAtual = this.formulario?.get('parceiro')?.value;
    // Mantém "Interno" (id -1) sempre; limpa parceiro que não pertence ao novo segmento.
    if (parceiroAtual && parceiroAtual.id !== -1 && segmento
        && !(parceiroAtual.segmentos || []).includes(segmento)) {
      this.formulario.get('parceiro')?.reset();
    }
  }

  // Tempo estimado trava após entrar em desenvolvimento (não pode mais ser alterado).
  get tempoEstimadoBloqueado(): boolean {
    if (this.tempoEstimadoBloqueadoPersistido) {
      return true;
    }
    const status = this.formulario?.get('status')?.value;
    return !!status
      && status !== StatusTicketEnum.BACKLOG
      && status !== StatusTicketEnum.EM_ANALISE;
  }

  abrirPreview(file: UploadedFile): void {
    if (this.isImageFile(file.name)) {
      this.imagemAmpliada = file.preview;
      this.nomeImagemAmpliada = file.name;
    }
  }

  fecharPreview(): void {
    this.imagemAmpliada = null;
    this.nomeImagemAmpliada = '';
  }

  formatarTamanho(file: UploadedFile): string {
    const bytes = file.file?.size || 0;
    if (!bytes) {
      return '';
    }
    const kb = bytes / 1024;
    if (kb < 1024) {
      return `${kb.toFixed(0)} KB`;
    }
    return `${(kb / 1024).toFixed(1)} MB`;
  }

  getDescricaoTipoTicket(tipo: TipoTicketEnum): string {
    return DescricaoTipoTicket.get(tipo) || '';
  }

  getDescricaoPrioridade(prioridade: PrioridadeEnum): string {
    return DescricaoPrioridade.get(prioridade) || '';
  }

  getDescricaoStatusTicket(status: StatusTicketEnum): string {
    return DescricaoStatusTicket.get(status) || '';
  }

  isImageFile(fileName: string): boolean {
    const imageExtensions = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'svg'];
    const fileExtension = fileName.split('.').pop()?.toLowerCase();
    return imageExtensions.includes(fileExtension || '');
  }
}
